/**
 * Device-alerts list (UX-002 AMD-001 §3; the P6 alerts tab when `site` is given).
 *
 * Every figure on this screen comes from `/v1/alerts` or is withheld with its
 * reason. The wireframe this screen was drawn from states in its own preamble
 * that "figures are illustrative and are not fixtures", so none of its numbers
 * are reproduced here: a fabricated alert figure is worse than no figure
 * (ruling 3.2/6.1/9, UX-003 §3.1).
 *
 * Declared seams, stated on screen rather than filled in:
 *  - acknowledgement: no ack store exists, so the server pins `acknowledged`
 *    false for every row and this screen reports NOT_MEASURED, not 0;
 *  - stopped capacity: computed server-side from `device_registry.rated_kw`,
 *    but `/v1/alerts` does not yet carry the field, so the figure is withheld
 *    here instead of being reconstructed from a per-inverter constant;
 *  - timestamps: the required basis is the site's own `platform_sites.timezone`,
 *    which this contract does not carry, so stamps name the zone they are shown
 *    in rather than silently using the reader's browser zone.
 */
import { useState, useMemo, useRef, useEffect, type ReactNode } from "react";

import { useGetV1Alerts, useGetV1Findings } from "@solarkey/api-client";
import type {
  EnvelopeDeviceAlertsDataAlertsItem,
  EnvelopeFindingsDataItemsItem,
  Figure,
  Reason,
} from "@solarkey/api-client";
import { EmptyState, Metric, Skeleton, StateWord, atDisplayScale, useLocale } from "@solarkey/ui";

import { SiteTabBar } from "../components/SiteTabBar";
import { BackButton } from "../shell/BackButton";

/** Severity filters. `info` (severity 0) is shown to match the wireframe; it
 *  filters to info-level alerts. The pipeline currently emits only 1..2, so the
 *  Info view is empty until the backend sends severity-0 alerts — an honest
 *  "no alerts match", not a fabricated row. */
type SeverityFilter = "all" | "critical" | "warning" | "info";
/** No `acknowledged` filter: nothing can be acknowledged (see the ack seam). */
type StateFilter = "all" | "active" | "cleared";
type DeviceClass = "all" | "inverter" | "logger" | "transformer" | "meter" | "weather";
type GroupBy = "site" | "device" | "none";
type SortBy = "stopped_capacity" | "severity" | "first_seen" | "count";
/** The window the screen asks for: the site-local calendar day, or a rolling
 *  duration in days. The two are resolved differently — see TIME_WINDOWS. */
type WindowSel = "today" | "yesterday" | 7 | 30 | "custom";

/** Where a stamp lands when the site carries no timezone at all. Named on screen
 *  whenever it is used, so a UTC stamp is never read as a local one. */
const ZONE_FALLBACK = "UTC";

/* ── Withheld-value reasons ──────────────────────────────────────────────────
   Templated, never free prose (UX-003 §3.8). Each states a true cause. */
const NO_ACK_STORE: Reason = { template: "NO_ACKNOWLEDGEMENT_STORE" };
const STOPPED_CAPACITY_NOT_IN_CONTRACT: Reason = { template: "STOPPED_CAPACITY_NOT_IN_CONTRACT" };
const NO_ALERTS_IN_WINDOW: Reason = { template: "NO_ALERTS_IN_WINDOW" };
const NO_SEVERITY_LABEL_SENT: Reason = { template: "NO_SEVERITY_LABEL_SENT" };
const INCONSISTENT_SEVERITY_SPLIT: Reason = { template: "INCONSISTENT_SEVERITY_SPLIT" };
const NO_ALERT_NAME_SENT: Reason = { template: "NO_ALERT_NAME_SENT" };

/* ── Why a control is disabled ───────────────────────────────────────────────
   Stated, following the Branding.tsx pattern: `disabled` plus the reason. */
const WHY_NO_ACK_ACTION = "Acknowledging needs an acknowledgement store and endpoint, neither of which exists yet.";
const WHY_NO_WORK_ORDERS = "Creating work orders needs a work-order endpoint this build does not have yet.";

interface SavedView {
  key: string;
  label: string;
  severity: SeverityFilter;
  state: StateFilter;
  deviceClass: DeviceClass;
}

const SAVED_VIEWS: SavedView[] = [
  { key: "critical_open", label: "critical open", severity: "critical", state: "active", deviceClass: "all" },
  { key: "all_open", label: "all open", severity: "all", state: "active", deviceClass: "all" },
  { key: "cleared", label: "cleared in window", severity: "all", state: "cleared", deviceClass: "all" },
  { key: "inverter_faults", label: "inverter faults", severity: "critical", state: "active", deviceClass: "inverter" },
];

/** The windows this screen offers.
 *
 *  `today` is the site-local CALENDAR day. A day only exists inside a timezone,
 *  and the required basis is each site's own, so the browser never computes it:
 *  the request carries `period=today` and the server resolves it from the
 *  registry, returning the window it actually read in `meta.period`. The chip sat
 *  disabled for want of that, saying the endpoint could not state a day — which
 *  stopped being true once the endpoint resolved one.
 *
 *  `yesterday` is resolved the same way and for the same reason, and it earns its
 *  place rather than being a convenience: just after local midnight "Today" is a
 *  window minutes wide holding nothing, and without a way back the screen looks
 *  broken at exactly the hour a night-shift operator opens it.
 *
 *  7D and 30D are ROLLING durations, true in any zone, so they are sent as an
 *  instant. `custom` carries two dates the viewer picked; the footer always states
 *  the window the server actually read, so a range is never left ambiguous. */
const TIME_WINDOWS: { key: WindowSel; label: string }[] = [
  { key: "today", label: "Today" },
  { key: "yesterday", label: "Yesterday" },
  { key: 7, label: "7D" },
  { key: 30, label: "30D" },
];

const DAY_MS = 86_400_000;

interface AlertsListProps {
  site?: string;
}

/* ── The Alerts page: the P6 two-door (UX-002). "Device alarms" is what the
      equipment itself reported — decoded inverter fault words off the telemetry
      (/v1/alerts). "Findings" is the platform's own rule-based reading of the
      plant (/v1/findings). The page chrome — title, the site tab row, the door
      toggle — is shared; each door owns its own data and controls below it. ── */
type Door = "device_alarms" | "findings";

export function AlertsList({ site }: AlertsListProps): ReactNode {
  // DEVICE ALARMS leads. It is what the plant actually said — 106 decoded faults
  // over a week, with the device, the code and its meaning. Findings is three
  // rules the platform runs over the same plant, and it was sitting in front of
  // the real thing: a reader opening Alerts during a fault saw three synthetic
  // rows and had to know to click a second tab to find the fault itself.
  const [door, setDoor] = useState<Door>("device_alarms");

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 10, padding: "14px 0 2px", flexWrap: "wrap" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <BackButton />
          <h2 style={pageTitleStyle}>Alerts</h2>
        </div>
        <span style={{ fontSize: 12, color: "var(--sk-color-ink-2)" }}>{site ?? "Portfolio"}</span>
      </div>

      {/* At site scope this is the site's Alerts tab, so it carries the site tab
          row — the same navigation every other site page shows. Absent at fleet scope. */}
      {site ? <SiteTabBar site={site} /> : null}

      <DoorTabs door={door} onDoor={setDoor} />

      {door === "findings"
        ? <FindingsView site={site} />
        : <DeviceAlarmsView site={site} />}
    </div>
  );
}

/* ── Door toggle: Findings | Device alarms (UX-002 P6) ──────────────── */
function DoorTabs({ door, onDoor }: { door: Door; onDoor: (d: Door) => void }): ReactNode {
  const tab = (key: Door, label: string): ReactNode => (
    <button
      type="button"
      onClick={() => onDoor(key)}
      aria-pressed={door === key}
      style={door === key ? doorTabActiveStyle : doorTabStyle}
    >
      {label}
    </button>
  );
  return (
    <div style={doorTabsStyle} role="tablist" aria-label="Alerts view">
      {tab("device_alarms", "Device alarms")}
      {tab("findings", "Findings")}
    </div>
  );
}

/* ── Device-alarms door: the raw /v1/alerts stream (the former Alerts screen) ── */
function DeviceAlarmsView({ site }: AlertsListProps): ReactNode {
  const locale = useLocale();
  const [severityFilter, setSeverityFilter] = useState<SeverityFilter>("critical");
  const [stateFilter, setStateFilter] = useState<StateFilter>("active");
  const [deviceClass, setDeviceClass] = useState<DeviceClass>("all");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [groupBy, setGroupBy] = useState<GroupBy>("site");
  const [sortBy, setSortBy] = useState<SortBy>("stopped_capacity");
  const [savedView, setSavedView] = useState("critical_open");
  // The window the chip claims is the window the request asks for — the chip used
  // to show "Today" while the query sent nothing and the server defaulted to 7 days.
  const [windowSel, setWindowSel] = useState<WindowSel>("today");

  // Two shapes, because the two windows are not the same kind of thing. A rolling
  // duration is an instant this browser can compute; the calendar day is named and
  // left to the server, which is the only side that knows each site's timezone.
  // A custom range is held as two local DATES, which is what a person picks. They
  // become instants only on the way out, and the footer prints the window the
  // server read back, so the two can never drift apart silently.
  const [customFrom, setCustomFrom] = useState<string>("");
  const [customTo, setCustomTo] = useState<string>("");

  const windowArgs = useMemo(() => {
    if (windowSel === "today" || windowSel === "yesterday") {
      return { period: windowSel };
    }
    if (windowSel === "custom") {
      // Until both ends are chosen, keep the last sensible window rather than
      // firing a request for a half-specified range.
      if (!customFrom || !customTo) return { from: new Date(Date.now() - 7 * DAY_MS).toISOString() };
      // `to` is exclusive in the read, so the chosen end DATE is included whole.
      const end = new Date(`${customTo}T00:00:00`);
      end.setDate(end.getDate() + 1);
      return { from: new Date(`${customFrom}T00:00:00`).toISOString(), to: end.toISOString() };
    }
    return { from: new Date(Date.now() - windowSel * DAY_MS).toISOString() };
  }, [windowSel, customFrom, customTo]);

  const query = useGetV1Alerts({ ...(site ? { site } : {}), ...windowArgs });

  if (query.isLoading) {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <BackButton />
          <h2 className="pagehead">Alerts</h2>
        </div>
        <Skeleton variant="row" lines={8} />
      </div>
    );
  }

  if (query.isError || !query.data || query.data.status !== 200) {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <BackButton />
          <h2 className="pagehead">Alerts</h2>
        </div>
        <div className="card">
          <span className="muted">Unable to load alerts.</span>
        </div>
      </div>
    );
  }

  const envelope = query.data.data;
  const data = envelope.data;
  const alerts = data.alerts;
  const period = envelope.meta.period;

  // Counts come from the server over ONE population each (UX-003 §3.7 rule 8):
  //   windowCount  — every distinct alert in the window, cleared included
  //   activeCount  — the still-open ones ("open")
  //   criticalCount / warningCount — the severity split of the ACTIVE population,
  //     so the two always sum to activeCount and the warning count cannot go
  //     negative (it could when the split was taken over a different population).
  const windowCount = data.count;
  const activeCount = data.active_count ?? 0;
  const criticalCount = data.critical_count ?? 0;
  // The split is the server's, over the active population, so the remainder is
  // the active warnings. If a response ever violates that (criticals outside the
  // active set), the remainder is meaningless — it is withheld, never shown as a
  // negative number and never clamped to 0, which would be a quiet invention.
  const warningCount = activeCount - criticalCount >= 0 ? activeCount - criticalCount : null;
  // Likewise: cleared = window total − still open. A response where the active
  // count exceeds the window total is self-inconsistent, so withhold.
  const clearedInWindow = windowCount - activeCount >= 0 ? windowCount - activeCount : null;
  const sitesInScope = data.sites_in_scope ?? 0;

  function handleExport(): void {
    exportAlertsCsv(alerts, severityFilter, stateFilter, deviceClass, locale);
  }

  function handleSavedView(key: string): void {
    const view = SAVED_VIEWS.find((v) => v.key === key);
    if (!view) return;
    setSavedView(key);
    setSeverityFilter(view.severity);
    setStateFilter(view.state);
    setDeviceClass(view.deviceClass);
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <PageHeader
        activeCount={activeCount}
        windowSel={windowSel}
        onWindowSel={setWindowSel}
        onExport={handleExport}
        customFrom={customFrom}
        customTo={customTo}
        onCustomFrom={setCustomFrom}
        onCustomTo={setCustomTo}
      />

      <SummaryCards
        activeCount={activeCount}
        criticalCount={criticalCount}
        warningCount={warningCount}
        clearedInWindow={clearedInWindow}
        sitesInScope={sitesInScope}
        stoppedCapacity={data.stopped_capacity}
        windowSel={windowSel}
        alerts={alerts}
      />

      <FilterBar
        severityFilter={severityFilter}
        onSeverityFilter={setSeverityFilter}
        stateFilter={stateFilter}
        onStateFilter={setStateFilter}
        deviceClass={deviceClass}
        onDeviceClass={setDeviceClass}
        groupBy={groupBy}
        onGroupBy={setGroupBy}
        sortBy={sortBy}
        onSortBy={setSortBy}
        savedView={savedView}
        onSavedView={handleSavedView}
      />

      <BulkBar
        selected={selected}
        alerts={alerts}
        onExport={handleExport}
        onClear={() => setSelected(new Set())}
      />

      <GroupedAlertsTable
        alerts={alerts}
        severityFilter={severityFilter}
        stateFilter={stateFilter}
        deviceClass={deviceClass}
        groupBy={groupBy}
        sortBy={sortBy}
        showSite={!site}
        selected={selected}
        onSelected={setSelected}
      />

      <Footer sitesInScope={sitesInScope} windowCount={windowCount} period={period}
        zone={sharedZone(alerts) ?? data.timezone ?? null} />
    </div>
  );
}

/* ── Page header ─────────────────────────────────────────────────── */

/* ── Findings door: the engine's rule-based verdicts from /v1/findings ──────────
      Verdict only — severity, a human title + one-line detail, class, opened time,
      value at stake (withheld until the value engine lands), and the engine's
      proposed action. No raw register content — that lives on the Device alarms
      door (UX-002 P6 two-door). Ranked most-severe first by the server. ── */
const FINDING_CLASS_LABEL: Record<string, string> = {
  data_path: "Data path",
  equipment: "Equipment",
  performance: "Performance",
};
function findingClassLabel(cls?: string): string {
  return cls ? (FINDING_CLASS_LABEL[cls] ?? sentenceCase(cls.replace(/_/g, " "))) : "";
}

const FINDING_ACTION_LABEL: Record<string, string> = {
  acknowledge: "Acknowledge",
  raise_work_order: "Raise work order",
};
function findingActionLabel(a?: string | null): string {
  return a ? (FINDING_ACTION_LABEL[a] ?? sentenceCase(a.replace(/_/g, " "))) : "—";
}

function FindingsView({ site }: { site?: string }): ReactNode {
  const locale = useLocale();
  const query = useGetV1Findings({ ...(site ? { site } : {}) });

  if (query.isLoading) return <Skeleton variant="row" lines={6} />;
  if (query.isError || !query.data || query.data.status !== 200) {
    return <div className="card"><span className="muted">Unable to load findings.</span></div>;
  }

  const data = query.data.data.data;
  const items = data.items ?? [];
  const summary = data.summary;

  if (items.length === 0) {
    return (
      <EmptyState
        variant="verified_quiet"
        title="No findings"
        message="No plant in this scope trips a platform rule. Faults the equipment itself reported are on the Device alarms tab."
      />
    );
  }

  const bySev = (summary?.by_severity ?? {}) as Record<string, number | undefined>;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <div className="muted" style={{ fontSize: 12 }}>
        {typeof summary?.total === "number" ? `${summary.total} finding${summary.total === 1 ? "" : "s"}` : null}
        {summary?.by_severity
          ? ` · ${bySev.high ?? 0} high, ${bySev.warning ?? 0} warning, ${bySev.info ?? 0} info`
          : null}
      </div>
      <table style={tableStyle}>
        <thead>
          <tr>
            <th style={thStyle}>Severity</th>
            <th style={thStyle}>Finding</th>
            {!site ? <th style={thStyle}>Site</th> : null}
            <th style={thStyle}>Class</th>
            <th style={thStyle} title="When the condition demonstrably began. Withheld where the platform cannot establish one — these rules are evaluated fresh on each read and not stored.">Since</th>
            <th style={thStyle}>At stake</th>
            <th style={thStyle}>Proposed</th>
          </tr>
        </thead>
        <tbody>
          {items.map((f, i) => (
            <FindingRow key={f.id ?? i} finding={f} showSite={!site} locale={locale} />
          ))}
        </tbody>
      </table>
      <div className="muted" style={{ fontSize: 11, padding: "4px 0", lineHeight: 1.5 }}>
        The platform's own rule-based reading of each plant, ranked most severe first —
        data path, devices not reporting, and performance against the low-PR threshold.
        What the EQUIPMENT reported is on the Device alarms tab.
      </div>
    </div>
  );
}

/** The site IDENTIFIER, made readable: "pimpalgaon" -> "Pimpalgaon".
 *
 *  Deliberately derived from the slug rather than the registry's `Site.name`,
 *  which is free text that may carry a plant or customer name (CLAUDE.md rule 5).
 *  Title-casing the identifier gives the same words with none of that exposure. */
function prettySite(slug?: string | null): string {
  return (slug ?? "")
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

function FindingRow({ finding, showSite, locale }: {
  finding: EnvelopeFindingsDataItemsItem; showSite: boolean; locale?: string;
}): ReactNode {
  const sev = (finding.severity ?? "").toLowerCase();
  const sevColor = sev === "high"
    ? "var(--sk-color-status-fault)"
    : sev === "warning"
      ? "var(--sk-color-status-warning)"
      : "var(--sk-color-ink-3)";
  const sevIcon = sev === "high" ? "■" : sev === "warning" ? "▲" : "●";
  const energy = finding.value_at_stake?.energy;

  return (
    <tr>
      <td style={tdStyle}>
        {finding.severity ? (
          <span style={{ color: sevColor, fontSize: 12, display: "inline-flex", alignItems: "center", gap: 4 }}>
            <span style={{ fontSize: 10 }}>{sevIcon}</span>
            {sentenceCase(finding.severity)}
          </span>
        ) : (
          <StateWord state="CANNOT_CHECK" />
        )}
      </td>
      <td style={tdStyle}>
        <div style={{ fontSize: 13 }}>{finding.title ?? <StateWord state="CANNOT_CHECK" />}</div>
        {finding.what ? <div className="muted" style={{ fontSize: 11, marginTop: 2 }}>{finding.what}</div> : null}
      </td>
      {showSite ? <td style={{ ...tdStyle, fontWeight: 600 }}>{prettySite(finding.site_label ?? finding.site_id)}</td> : null}
      <td style={{ ...tdStyle, fontSize: 12 }}>{findingClassLabel(finding.class)}</td>
      <td style={{ ...tdStyle, fontSize: 12, whiteSpace: "nowrap" }}>
        {/* Only the data-path rule knows when its condition began (the last packet).
            The others are read-time evaluations of a continuous state, so there is
            no instant to report — and the column used to print `now()`, which moved
            on every refresh. Withheld beats invented. */}
        {finding.opened_at
          ? formatStamp(finding.opened_at, undefined, locale)
          : <StateWord state="NOT_MEASURED"
                       reason={{ template: "NOT_MEASURED_CONDITION_NOT_TIMESTAMPED" }} />}
      </td>
      <td style={tdStyle}>
        {energy && typeof energy.value === "number"
          ? <Metric figure={atDisplayScale(energy) ?? energy} showBasis={false} showProvenance={false} showFreshness={false} />
          : <StateWord state={energy?.state ?? "NOT_MEASURED"} reason={energy?.reason} />}
      </td>
      <td style={{ ...tdStyle, fontSize: 12, color: "var(--sk-color-ink-3)" }}>
        {findingActionLabel(finding.primary_action)}
      </td>
    </tr>
  );
}

const doorTabsStyle: React.CSSProperties = {
  display: "flex",
  gap: 4,
  borderBottom: "1px solid var(--sk-color-border-2)",
};
const doorTabStyle: React.CSSProperties = {
  background: "none",
  border: 0,
  borderBottom: "2px solid transparent",
  padding: "6px 12px",
  fontSize: 13,
  fontWeight: 500,
  color: "var(--sk-color-ink-3)",
  cursor: "pointer",
};
const doorTabActiveStyle: React.CSSProperties = {
  ...doorTabStyle,
  color: "var(--sk-color-ink-1)",
  borderBottom: "2px solid var(--sk-color-accent)",
};

/** Today as yyyy-mm-dd in the VIEWER's zone — the bound a date input needs so a
 *  future day cannot be picked. The window the server reads is still each site's
 *  own day; this only stops the picker offering tomorrow. */
function todayISO(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

const dateInputStyle: React.CSSProperties = {
  fontSize: 12,
  padding: "3px 6px",
  border: "1px solid var(--sk-color-border-2)",
  borderRadius: 6,
  background: "var(--sk-color-surface-0)",
  color: "var(--sk-color-ink-1)",
};

function PageHeader({ activeCount, windowSel, onWindowSel, onExport,
                     customFrom, customTo, onCustomFrom, onCustomTo }: {
  activeCount: number;
  windowSel: WindowSel;
  onWindowSel: (w: WindowSel) => void;
  onExport: () => void;
  customFrom: string;
  customTo: string;
  onCustomFrom: (v: string) => void;
  onCustomTo: (v: string) => void;
}): ReactNode {
  // The device-alarms door toolbar. The page title + scope live in the shared
  // chrome above the door toggle; here it is the active count + window + export.
  const subtitle = `${activeCount} open · device-reported`;

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "2px 0 6px", flexWrap: "wrap" }}>
      <span style={{ fontSize: 12, color: "var(--sk-color-ink-2)", whiteSpace: "nowrap", marginRight: "auto" }}>{subtitle}</span>
      <div style={timeCtlStyle} role="group" aria-label="Time window">
        {TIME_WINDOWS.map((w) => (
          <button
            key={String(w.key)}
            type="button"
            style={windowSel === w.key ? timeCtlActiveStyle : timeCtlItemStyle}
            aria-pressed={windowSel === w.key}
            onClick={() => onWindowSel(w.key)}
          >
            {w.label}
          </button>
        ))}
        <button
          type="button"
          style={windowSel === "custom" ? timeCtlActiveStyle : timeCtlItemStyle}
          aria-pressed={windowSel === "custom"}
          onClick={() => onWindowSel("custom")}
        >
          Custom
        </button>
      </div>
      {/* The two ends appear only once Custom is chosen, so the control row stays
          quiet for the common case. `max` stops a future date being picked — a
          window that has not happened yet would return nothing and read as a fault
          in the data rather than a fault in the question. */}
      {windowSel === "custom" ? (
        <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12 }}>
          <label style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <span className="muted">from</span>
            <input type="date" value={customFrom} max={customTo || todayISO()}
                   onChange={(e) => onCustomFrom(e.target.value)}
                   style={dateInputStyle} aria-label="Range start date" />
          </label>
          <label style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <span className="muted">to</span>
            <input type="date" value={customTo} min={customFrom || undefined} max={todayISO()}
                   onChange={(e) => onCustomTo(e.target.value)}
                   style={dateInputStyle} aria-label="Range end date" />
          </label>
          {!customFrom || !customTo ? (
            <span className="muted">pick both ends — showing the last 7 days meanwhile</span>
          ) : null}
        </div>
      ) : null}
      <button type="button" style={exportBtnStyle} onClick={onExport}>
        Export CSV
      </button>
    </div>
  );
}

/* ── Summary cards ───────────────────────────────────────────────── */

function SummaryCards({
  activeCount,
  criticalCount,
  warningCount,
  clearedInWindow,
  stoppedCapacity,
  sitesInScope,
  windowSel,
  alerts,
}: {
  activeCount: number;
  criticalCount: number;
  warningCount: number | null;
  clearedInWindow: number | null;
  sitesInScope: number;
  stoppedCapacity?: Figure;
  windowSel: WindowSel;
  alerts: EnvelopeDeviceAlertsDataAlertsItem[];
}): ReactNode {
  const activeAlerts = alerts.filter((a) => a.active);
  const sitesAffected = new Set(activeAlerts.map((a) => a.site)).size;

  const noisiest = alerts.reduce<EnvelopeDeviceAlertsDataAlertsItem | null>((max, a) =>
    !max || (a.count ?? 0) > (max.count ?? 0) ? a : max, null);

  return (
    <div style={summaryGridStyle}>
      <SummaryCard
        label="OPEN"
        value={String(activeCount)}
        detail={
          <>
            {criticalCount} critical ·{" "}
            {warningCount === null
              ? <StateWord state="CANNOT_CHECK" reason={INCONSISTENT_SEVERITY_SPLIT} />
              : `${warningCount} warning`}
          </>
        }
      />

      {/* The server pins `acknowledged` false on every row because no ack store
          exists. Counting those falses would render a computed 0 for a declared
          seam, which reads as "nobody has acknowledged anything". UX-REV-001 U2:
          the withheld state stays (reason rides in the StateWord tooltip), but
          the build-status sentence is off the glass — the detail now names the
          metric, not the backend gap. */}
      <SummaryCard
        label="ACKNOWLEDGED"
        value={<StateWord state="NOT_MEASURED" reason={NO_ACK_STORE} />}
        detail="who acknowledged, and when"
        valueSmall
      />

      {/* The sum of `device_registry.rated_kw` over the devices with an open
          critical alert, computed server-side in alarms.py. It was withheld here
          because the contract did not declare the field, so a generated client
          had nothing to read — the server had been sending it all along. The
          figure is still the server's: withheld states print as themselves. */}
      <SummaryCard
        label="STOPPED CAPACITY"
        value={stoppedCapacity && typeof stoppedCapacity.value === "number"
          ? <Metric figure={atDisplayScale(stoppedCapacity) ?? stoppedCapacity}
                    showBasis={false} showProvenance={false} showFreshness={false} />
          : <StateWord state={stoppedCapacity?.state ?? "NOT_MEASURED"}
                       reason={stoppedCapacity?.reason ?? STOPPED_CAPACITY_NOT_IN_CONTRACT} />}
        detail="plated capacity behind the open critical alerts"
        valueSmall
      />

      {/* The same population the rest of the screen uses, over whichever window
          the chips asked for — the detail line names that window rather than a
          fixed duration, which read as a 7-day figure on every other setting. */}
      <SummaryCard
        label="CLEARED IN WINDOW"
        value={clearedInWindow === null
          ? <StateWord state="CANNOT_CHECK" reason={INCONSISTENT_SEVERITY_SPLIT} />
          : String(clearedInWindow)}
        detail={`alerts that closed inside ${windowSel === "today"
          ? "today (each site's own day)"
          : `the ${windowSel}-day window`}`}
        valueSmall={clearedInWindow === null}
      />

      <SummaryCard label="SITES AFFECTED" value={`${sitesAffected} of ${sitesInScope}`}
        detail="sites with at least one open alert" />

      <SummaryCard label="NOISIEST DEVICE"
        value={noisiest
          ? `${noisiest.site} · ${noisiest.device}`
          : <StateWord state="NOT_APPLICABLE" reason={NO_ALERTS_IN_WINDOW} />}
        detail={noisiest
          // The fault's NAME, like every row below. This card was the last place
          // still printing the gateway's raw field name at a reader.
          ? `${noisiest.count} events in window · ${noisiest.label ?? noisiest.event_type ?? ""}`
          : ""} valueSmall />
    </div>
  );
}

function SummaryCard({ label, value, detail, valueSmall }: {
  label: string; value: ReactNode; detail: ReactNode; valueSmall?: boolean;
}): ReactNode {
  return (
    <div className="card" style={summaryCardStyle}>
      <span style={summaryLabelStyle}>{label}</span>
      <span style={{ ...summaryValueStyle, fontSize: valueSmall ? 14 : 21 }}>{value}</span>
      <span style={{ fontSize: 11, lineHeight: 1.3, color: "var(--sk-color-ink-2)", display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>{detail}</span>
    </div>
  );
}

/* ── Filter bar ──────────────────────────────────────────────────── */

const GROUP_OPTIONS = [
  { key: "site", label: "Site" },
  { key: "device", label: "Device class" },
  { key: "none", label: "None" },
];

/** "Stopped capacity" is the wireframe's default sort, and it now sorts by what
 *  it says: each row carries its device's plated rating (`rated_kw`) from the
 *  registry, so the ranking is severity first, then the capacity standing behind
 *  the alert. It used to order by event count as a proxy, because the field was
 *  not in the contract — the server was computing the scope total from exactly
 *  these ratings the whole time. */
const SORT_OPTIONS = [
  { key: "stopped_capacity", label: "Stopped capacity" },
  { key: "severity", label: "Severity" },
  { key: "first_seen", label: "First seen" },
  { key: "count", label: "Count" },
];

function FilterBar({
  severityFilter, onSeverityFilter,
  stateFilter, onStateFilter,
  deviceClass, onDeviceClass,
  groupBy, onGroupBy,
  sortBy, onSortBy,
  savedView, onSavedView,
}: {
  severityFilter: SeverityFilter; onSeverityFilter: (f: SeverityFilter) => void;
  stateFilter: StateFilter; onStateFilter: (f: StateFilter) => void;
  deviceClass: DeviceClass; onDeviceClass: (f: DeviceClass) => void;
  groupBy: GroupBy; onGroupBy: (g: GroupBy) => void;
  sortBy: SortBy; onSortBy: (s: SortBy) => void;
  savedView: string; onSavedView: (v: string) => void;
}): ReactNode {
  const currentSort = SORT_OPTIONS.find((o) => o.key === sortBy);
  const currentGroup = GROUP_OPTIONS.find((o) => o.key === groupBy);
  const currentView = SAVED_VIEWS.find((v) => v.key === savedView);

  return (
    <div style={filterRowStyle}>
      {/* The chips carry no counts: they filter the whole window while the
          server's split is over the active population, so a count here would
          disagree with the rows below. The OPEN card states the split. */}
      <FilterChip active={severityFilter === "all"} onClick={() => onSeverityFilter("all")}>All severities</FilterChip>
      <FilterChip active={severityFilter === "critical"} onClick={() => onSeverityFilter("critical")}>Critical</FilterChip>
      <FilterChip active={severityFilter === "warning"} onClick={() => onSeverityFilter("warning")}>Warning</FilterChip>
      <FilterChip active={severityFilter === "info"} onClick={() => onSeverityFilter("info")}>Info</FilterChip>

      <span className="muted" style={filterLabelStyle}>State</span>
      <FilterChip active={stateFilter === "all"} onClick={() => onStateFilter("all")} label="All states">All</FilterChip>
      <FilterChip active={stateFilter === "active"} onClick={() => onStateFilter("active")}>Active</FilterChip>
      <FilterChip active={stateFilter === "cleared"} onClick={() => onStateFilter("cleared")}>Cleared</FilterChip>
      <FilterChip active={false} disabled why={WHY_NO_ACK_ACTION} onClick={() => undefined}>Acknowledged</FilterChip>

      <span className="muted" style={filterLabelStyle}>Device class</span>
      <FilterChip active={deviceClass === "all"} onClick={() => onDeviceClass("all")} label="All device classes">All</FilterChip>
      <FilterChip active={deviceClass === "inverter"} onClick={() => onDeviceClass("inverter")}>Inverter</FilterChip>
      <FilterChip active={deviceClass === "logger"} onClick={() => onDeviceClass("logger")}>Logger</FilterChip>
      <FilterChip active={deviceClass === "transformer"} onClick={() => onDeviceClass("transformer")}>Transformer</FilterChip>
      <FilterChip active={deviceClass === "meter"} onClick={() => onDeviceClass("meter")}>Meter</FilterChip>
      <FilterChip active={deviceClass === "weather"} onClick={() => onDeviceClass("weather")}>Weather station</FilterChip>

      <span className="muted" style={filterLabelStyle}>Group</span>
      <ChipDropdown
        label={currentGroup?.label ?? "Site"}
        options={GROUP_OPTIONS}
        value={groupBy}
        onChange={(k) => onGroupBy(k as GroupBy)}
      />

      <span className="muted" style={filterLabelStyle}>Sort</span>
      <ChipDropdown
        label={currentSort?.label ?? "Severity"}
        options={SORT_OPTIONS}
        value={sortBy}
        onChange={(k) => onSortBy(k as SortBy)}
      />

      <ChipDropdown
        label={`Saved view: ${currentView?.label ?? "critical open"}`}
        options={SAVED_VIEWS.map((v) => ({ key: v.key, label: v.label }))}
        value={savedView}
        onChange={onSavedView}
      />
    </div>
  );
}

function FilterChip({ active, onClick, disabled, why, label, children }: {
  active: boolean; onClick: () => void;
  disabled?: boolean; why?: string;
  /** What this chip is called when its text alone does not say — three groups on
   *  this bar each hold an "All", which is three identically-named controls to
   *  anyone not reading the label beside them. */
  label?: string;
  children: ReactNode;
}): ReactNode {
  const style: React.CSSProperties = disabled
    ? { ...chipBase, color: "var(--sk-color-ink-3)", cursor: "not-allowed" }
    : active
      ? { ...chipBase, background: "var(--sk-color-accent-soft)", borderColor: "var(--sk-color-accent)", color: "var(--sk-color-accent)", fontWeight: 600 }
      : { ...chipBase };
  return (
    <button
      type="button"
      style={style}
      onClick={onClick}
      disabled={disabled}
      title={why}
      aria-label={label}
      aria-disabled={disabled ? "true" : undefined}
    >
      {children}
    </button>
  );
}

/* ── Chip dropdown ──────────────────────────────────────────────── */

function ChipDropdown({ label, options, value, onChange }: {
  label: string;
  options: { key: string; label: string }[];
  value: string;
  onChange: (key: string) => void;
}): ReactNode {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  return (
    // inline-flex + center so the chip sits on the same line as its label and the
    // filter chips — a plain block wrapper let the 18px chip drop ~3px below them.
    <div style={{ position: "relative", display: "inline-flex", alignItems: "center" }} ref={ref}>
      <button
        type="button"
        style={{ ...chipBase, fontWeight: 500, display: "inline-flex", alignItems: "center", gap: 4 }}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="listbox"
      >
        {label}
        {/* Wireframe chip caret: the ▾ sits inline at the chip's own size (not a
            tiny faded glyph), matching `.chip … ▾`. */}
        <span aria-hidden style={{ fontSize: 9 }}>&#9662;</span>
      </button>
      {open ? (
        <>
          <button
            type="button"
            aria-label="Close"
            style={{ position: "fixed", inset: 0, background: "transparent", border: 0, zIndex: 79 }}
            onClick={() => setOpen(false)}
          />
          <div style={dropdownMenuStyle} role="listbox">
            {options.map((opt) => (
              <button
                key={opt.key}
                type="button"
                role="option"
                aria-selected={opt.key === value}
                style={{
                  ...dropItemStyle,
                  fontWeight: opt.key === value ? 600 : 400,
                  background: opt.key === value ? "var(--sk-color-accent-soft)" : "transparent",
                }}
                onClick={() => { onChange(opt.key); setOpen(false); }}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </>
      ) : null}
    </div>
  );
}

/* ── Bulk action bar ─────────────────────────────────────────────── */

function BulkBar({ selected, alerts, onExport, onClear }: {
  selected: Set<string>;
  alerts: EnvelopeDeviceAlertsDataAlertsItem[];
  onExport: () => void;
  onClear: () => void;
}): ReactNode {
  if (selected.size === 0) return null;

  // No stopped-capacity figure here either: the old one multiplied the selection
  // by an invented capacity per inverter (a third constant, disagreeing with the
  // other two the screen used).
  return (
    <div style={bulkBarStyle}>
      <span style={{ fontSize: 12, fontWeight: 600, color: "var(--sk-color-ink-1)" }}>
        {selected.size} of {alerts.length} selected
      </span>
      <button
        type="button"
        className="btn"
        style={disabledActionStyle}
        disabled
        title={WHY_NO_ACK_ACTION}
        aria-disabled="true"
      >
        Acknowledge
      </button>
      <button type="button" className="btn" style={{ fontSize: 12, padding: "4px 12px" }} onClick={onExport}>
        Export
      </button>
      <button
        type="button"
        className="btn"
        style={disabledActionStyle}
        disabled
        title={WHY_NO_WORK_ORDERS}
        aria-disabled="true"
      >
        Create work orders
      </button>
      <span style={{ fontSize: 11, color: "var(--sk-color-ink-3)" }}>
        acknowledge and work orders have no endpoint in this build
      </span>
      <button type="button" style={{ background: "none", border: 0, cursor: "pointer", color: "var(--sk-color-ink-3)", fontSize: 11 }}
        onClick={onClear}>&#x2715;</button>
    </div>
  );
}

/* ── Grouped alerts table ────────────────────────────────────────── */

interface SiteGroup {
  groupLabel: string;
  alerts: (EnvelopeDeviceAlertsDataAlertsItem & { _idx: number })[];
  openCount: number;
  /** Stopped capacity for this group = the sum of `rated_kw` over its OPEN
   *  CRITICAL alerts (the same population the STOPPED CAPACITY tile sums
   *  portfolio-wide). Only rows that carry a numeric `rated_kw` are added;
   *  `stoppedKnownCount` < `stoppedCriticalActive` means some stopped device
   *  has no rating, so the figure is a floor (rendered with a ≥). */
  stoppedKw: number;
  stoppedKnownCount: number;
  stoppedCriticalActive: number;
  /** A contributing row's `rated_kw`, kept so the summed value renders through
   *  Metric with the server's own unit, basis and state — the grammar never
   *  lets a number carry a hardcoded unit (UX-003). */
  stoppedSample?: Figure;
}

function GroupedAlertsTable({
  alerts, severityFilter, stateFilter, deviceClass, groupBy, sortBy, showSite, selected, onSelected,
}: {
  alerts: EnvelopeDeviceAlertsDataAlertsItem[];
  severityFilter: SeverityFilter; stateFilter: StateFilter; deviceClass: DeviceClass;
  groupBy: GroupBy; sortBy: SortBy;
  showSite: boolean;
  selected: Set<string>; onSelected: (s: Set<string>) => void;
}): ReactNode {
  const locale = useLocale();
  const filtered = useMemo(() => {
    let list = alerts.map((a, i) => ({ ...a, _idx: i }));

    if (severityFilter === "critical") list = list.filter((a) => a.severity === 2);
    else if (severityFilter === "warning") list = list.filter((a) => a.severity === 1);
    else if (severityFilter === "info") list = list.filter((a) => a.severity === 0);

    if (stateFilter === "active") list = list.filter((a) => a.active);
    else if (stateFilter === "cleared") list = list.filter((a) => !a.active);

    if (deviceClass !== "all") {
      list = list.filter((a) => matchesDeviceClass(a.device, deviceClass));
    }

    // Newest first. Every sort falls back to this, because when its own key
    // cannot separate two rows the order a person reads a list by is time — and
    // a tie-break on event count put a 2-event alert from 06:22 above four
    // alerts from 12:04, which reads as the list being out of order.
    const recent = (x: typeof alerts[0], y: typeof alerts[0]): number =>
      (y.last_seen ?? "").localeCompare(x.last_seen ?? "");

    /** What this row actually holds at a standstill.
     *
     *  Stopped capacity is the capacity behind an OPEN CRITICAL alert — the same
     *  population the summary card sums. This sorted on the device's plate
     *  rating whatever the row's state, so a cleared warning ranked by how big
     *  its inverter is, which stops nothing. A row that is stopped but whose
     *  rating the registry cannot answer for ranks below every known figure and
     *  above everything that stops nothing: unknown is not zero, but it is not a
     *  claim either. */
    const stoppedKw = (x: typeof alerts[0]): number => {
      if (!x.active || (x.severity ?? 0) < 2) return -1;
      const kw = x.rated_kw?.value;
      return typeof kw === "number" ? kw : -0.5;
    };

    list.sort((a, b) => {
      // Still raised outranks cleared under every sort: an open fault is the
      // reason this screen exists.
      const open = Number(b.active ?? false) - Number(a.active ?? false);
      if (open !== 0) return open;

      if (sortBy === "first_seen") {
        const byFirst = (b.first_seen ?? "").localeCompare(a.first_seen ?? "");
        return byFirst !== 0 ? byFirst : recent(a, b);
      }
      if (sortBy === "count") {
        const byCount = (b.count ?? 0) - (a.count ?? 0);
        return byCount !== 0 ? byCount : recent(a, b);
      }
      // severity and stopped_capacity both lead with severity.
      const sev = (b.severity ?? 0) - (a.severity ?? 0);
      if (sev !== 0) return sev;
      if (sortBy === "stopped_capacity") {
        const byCapacity = stoppedKw(b) - stoppedKw(a);
        if (byCapacity !== 0) return byCapacity;
      }
      return recent(a, b);
    });

    return list;
  }, [alerts, severityFilter, stateFilter, deviceClass, sortBy]);

  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const groups = useMemo(() => {
    if (groupBy === "none") {
      // Ungrouped: one flat list, no group header renders, so the per-group
      // stopped-capacity fields stay zero (the STOPPED CAPACITY tile carries
      // the portfolio figure for this view).
      const g: SiteGroup = {
        groupLabel: "", alerts: filtered, openCount: 0,
        stoppedKw: 0, stoppedKnownCount: 0, stoppedCriticalActive: 0,
      };
      g.openCount = filtered.filter((a) => a.active).length;
      return [g];
    }

    const map = new Map<string, SiteGroup>();

    for (const a of filtered) {
      const key = groupBy === "device" ? classifyDevice(a.device) : (a.site ?? "Unknown");
      if (!map.has(key)) {
        map.set(key, {
          groupLabel: key, alerts: [], openCount: 0,
          stoppedKw: 0, stoppedKnownCount: 0, stoppedCriticalActive: 0,
        });
      }
      const g = map.get(key)!;
      g.alerts.push(a);
      if (a.active) g.openCount++;
      // Stopped capacity: an open critical alert means the device is down, so
      // its plated rating is capacity at a standstill. Sum the rows that carry
      // a real rating; the old header multiplied by an invented constant, which
      // is why it was removed — this adds back the real figure now that each
      // row carries `rated_kw` from the registry.
      if (a.active && a.severity === 2) {
        g.stoppedCriticalActive++;
        const kw = a.rated_kw?.value;
        if (typeof kw === "number") {
          g.stoppedKw += kw;
          g.stoppedKnownCount++;
          if (!g.stoppedSample) g.stoppedSample = a.rated_kw;
        }
      }
    }

    return Array.from(map.values());
  }, [filtered, groupBy]);

  if (filtered.length === 0) {
    // "Nothing open" and "nothing happened" are different answers, and the bare
    // filter message gave the second when it meant the first — which, now that
    // `active` is read from the register instead of a field nothing writes, is
    // the usual case: these faults fire and recover within minutes.
    const clearedHere = alerts.filter((a) => !a.active).length;
    const nothingOpen = stateFilter === "active" && clearedHere > 0;
    return (
      <div className="card" style={{ padding: 20 }}>
        <span className="muted" style={{ fontSize: 14 }}>
          {nothingOpen
            ? `Nothing is open. ${clearedHere} alert${clearedHere === 1 ? "" : "s"} `
              + "fired in this window and every one of them cleared."
            : "No alerts match the current filters."}
        </span>
      </div>
    );
  }

  function toggleSelect(idx: number): void {
    const key = String(idx);
    const next = new Set(selected);
    if (next.has(key)) next.delete(key); else next.add(key);
    onSelected(next);
  }

  // A header may name one zone only when every stamp below it is in that zone;
  // across an estate that spans several, it says so instead of picking one.
  const shared = sharedZone(filtered);
  // Which site sections are open. Closed to start when there is more than one,
  // so the screen opens on "which sites have something" rather than on every
  // row of every site at once; a single group has nothing to choose between and
  // is always open.
  const single = groups.length <= 1;
  const isOpen = (key: string): boolean => single || expanded.has(key);
  function toggleGroup(key: string): void {
    const next = new Set(expanded);
    if (next.has(key)) next.delete(key); else next.add(key);
    setExpanded(next);
  }
  const stampHead = shared ? zoneLabel(shared, locale) : "site local";

  return (
    <div className="card" style={{ overflow: "auto", padding: 0 }}>
      {groupBy !== "none" && !single ? (
        <div style={groupBarStyle}>
          <span>{groups.length} {groupBy === "site" ? "sites" : "devices"} · {filtered.length} alerts</span>
          <button
            type="button"
            style={linkButtonStyle}
            onClick={() => setExpanded(
              expanded.size === groups.length
                ? new Set()
                : new Set(groups.map((g) => g.groupLabel)),
            )}
          >
            {expanded.size === groups.length ? "Collapse all" : "Expand all"}
          </button>
        </div>
      ) : null}
      <table style={tableStyle}>
        <thead>
          <tr>
            <th style={thStyle}></th>
            <th style={thStyle}>SEVERITY</th>
            {showSite && <th style={thStyle}>SITE</th>}
            <th style={thStyle}>DEVICE</th>
            <th style={thStyle}>ALERT</th>
            <th style={{ ...thStyle, textAlign: "right" }}>COUNT</th>
            <th style={thStyle}>FIRST SEEN ({stampHead})</th>
            <th style={thStyle}>LAST SEEN ({stampHead})</th>
            <th style={thStyle}>STATE</th>
            <th style={thStyle}>ACKNOWLEDGED</th>
          </tr>
        </thead>
        <tbody>
          {groups.map((group) =>
            groupBy === "none" ? (
              group.alerts.map((alert) => (
                <AlertRow
                  key={`${alert.site}-${alert.device}-${alert.code}-${alert._idx}`}
                  alert={alert}
                  showSite={showSite}
                  isSelected={selected.has(String(alert._idx))}
                  onToggle={() => toggleSelect(alert._idx)}
                />
              ))
            ) : (
              <SiteGroupRows
                key={group.groupLabel}
                group={group}
                showSite={showSite}
                open={isOpen(group.groupLabel)}
                onToggleGroup={() => toggleGroup(group.groupLabel)}
                selected={selected}
                onToggle={toggleSelect}
              />
            ),
          )}
        </tbody>
      </table>
    </div>
  );
}

function SiteGroupRows({ group, showSite, open, onToggleGroup, selected, onToggle }: {
  group: SiteGroup; showSite: boolean;
  open: boolean; onToggleGroup: () => void;
  selected: Set<string>; onToggle: (idx: number) => void;
}): ReactNode {
  const colSpan = showSite ? 10 : 9;
  return (
    <>
      <tr>
        <td colSpan={colSpan} style={siteGroupHeaderStyle}>
          {/* The whole header is the control, so the section opens wherever a
              person aims at it. The count is on the header because a closed
              section has to say what is inside it to be worth closing. */}
          <button
            type="button"
            onClick={onToggleGroup}
            aria-expanded={open}
            style={groupToggleStyle}
          >
            <span aria-hidden="true" style={{ fontSize: 9, width: 9, display: "inline-block" }}>
              {open ? "▼" : "▶"}
            </span>
            <span style={{ fontWeight: 600 }}>{group.groupLabel}</span>
          </button>
          <span style={{ fontWeight: 400, color: "var(--sk-color-ink-2)" }}>
            {" "}· {group.alerts.length} alert{group.alerts.length === 1 ? "" : "s"}
            {" "}· {group.openCount} open
            {group.stoppedKnownCount > 0 && group.stoppedSample ? (
              <>
                {" "}· {group.stoppedKnownCount < group.stoppedCriticalActive ? "≥ " : ""}
                <span style={stoppedMetricStyle}>
                  <Metric
                    figure={atDisplayScale({ ...group.stoppedSample, value: group.stoppedKw })
                      ?? { ...group.stoppedSample, value: group.stoppedKw }}
                    showBasis={false}
                    showProvenance={false}
                    showFreshness={false}
                    maximumFractionDigits={1}
                  />
                </span>
                {" "}stopped
              </>
            ) : null}
          </span>
        </td>
      </tr>
      {open ? group.alerts.map((alert) => (
        <AlertRow
          key={`${alert.site}-${alert.device}-${alert.code}-${alert._idx}`}
          alert={alert}
          showSite={showSite}
          isSelected={selected.has(String(alert._idx))}
          onToggle={() => onToggle(alert._idx)}
        />
      )) : null}
    </>
  );
}

function AlertRow({ alert, showSite, isSelected, onToggle }: {
  alert: EnvelopeDeviceAlertsDataAlertsItem & { _idx: number };
  showSite: boolean; isSelected: boolean; onToggle: () => void;
}): ReactNode {
  const isCritical = alert.severity === 2;
  const severityIcon = isCritical ? "■" : "▲";
  const severityColor = isCritical
    ? "var(--sk-color-status-fault)"
    : "var(--sk-color-status-warning)";

  const locale = useLocale();
  // What the row says underneath the name: where the flag came from, and which
  // bit it was — the provenance a person CHASES. It no longer repeats the name
  // or the raw word, which the row printed three times between them.
  const bit = alert.measured_values?.bit;
  const provenance = [
    // The code on the inverter's own panel, first: it is what an engineer
    // standing at the machine reads, so it is what matches a row to a device.
    alert.display_code,
    alert.register,
    bit == null ? null : `bit ${bit}`,
    // The Alarm-flag register states the LEVEL raised, not which fault raised
    // it, so its row restates one reported in full on its own row. Said, not
    // hidden — it is the only signal when the fault sits in an undecoded word.
    alert.summary ? "summary register" : null,
  ].filter(Boolean).join(" · ");

  return (
    <tr style={isSelected ? { background: "var(--sk-color-accent-soft)" } : undefined}>
      <td style={tdStyle}>
        <input type="checkbox" checked={isSelected} onChange={onToggle}
          style={{ accentColor: "var(--sk-color-accent)" }} />
      </td>
      <td style={tdStyle}>
        {/* The server already classifies severity and sends its word; the screen
            no longer invents a label from the integer. */}
        {alert.severity_label ? (
          // The manufacturer's own grading where we hold its document — "Major
          // fault" says what the machine DID (stopped, disconnected, waiting for
          // a person), which "Warn" never could. Our own word is the fall-back
          // for a device whose document we do not have.
          <span
            style={{ color: severityColor, fontSize: 12, display: "inline-flex", alignItems: "center", gap: 4 }}
            title={alert.fault_level_meaning ?? undefined}
          >
            <span style={{ fontSize: 10 }}>{severityIcon}</span>
            {alert.fault_level_label ?? sentenceCase(alert.severity_label)}
          </span>
        ) : (
          <StateWord state="CANNOT_CHECK" reason={NO_SEVERITY_LABEL_SENT} />
        )}
      </td>
      {showSite && <td style={{ ...tdStyle, fontWeight: 600 }}>{alert.site ?? ""}</td>}
      <td style={tdStyle}>{alert.device ?? ""}</td>
      <td style={tdStyle}>
        <div>
          {/* The fault, as the device names it — composed on the server so the
              list, the export and any report cannot name one event three ways.
              The row used to print the decoded flag, the register, and then
              `bit · raw · name` beneath, which said the name twice and the
              register twice and read as machine output. */}
          {alert.label ? (
            <span style={{ fontSize: 13 }}>{alert.label}</span>
          ) : (
            <StateWord state="CANNOT_CHECK" reason={NO_ALERT_NAME_SENT} />
          )}
          {provenance ? (
            <div className="muted" style={{ fontSize: 11, marginTop: 2 }}>{provenance}</div>
          ) : null}
        </div>
      </td>
      {/* COUNT and FIRST SEEN are both WINDOW figures. A flag already raised
          when the window began produced no row inside it, so it has neither —
          and a 0 and a blank cell would read as "never happened" for the one
          row that is still happening. */}
      <td style={{ ...tdStyle, textAlign: "right", fontFamily: "var(--sk-font-mono)", fontVariantNumeric: "tabular-nums" }}>
        {alert.raised_before_window ? <span className="muted">—</span> : alert.count ?? 1}
      </td>
      <td style={{ ...tdStyle, fontSize: 12, whiteSpace: "nowrap" }}>
        {alert.raised_before_window
          ? <span className="muted">before this window</span>
          : formatStamp(alert.first_seen, alert.timezone, locale)}
      </td>
      <td style={{ ...tdStyle, fontSize: 12, whiteSpace: "nowrap" }}>
        {formatLastSeen(alert.last_seen, alert.timezone, locale)}
      </td>
      <td style={tdStyle}>
        <span style={{
          display: "inline-block",
          padding: "2px 8px",
          borderRadius: 4,
          fontSize: 11,
          fontWeight: 500,
          background: alert.active ? "var(--sk-color-surface-2)" : "transparent",
          color: alert.active ? "var(--sk-color-ink-1)" : "var(--sk-color-ink-3)",
          border: alert.active ? "none" : "1px solid var(--sk-color-border-2)",
        }}>
          {alert.active
            ? "Active"
            : `Cleared ${formatStamp(alert.cleared_at, alert.timezone, locale)}`}
        </span>
      </td>
      <td style={{ ...tdStyle, fontSize: 12, color: "var(--sk-color-ink-2)" }}>
        {/* Was a fabricated person and time. There is no ack store, so no row can
            name an owner — the column states the seam instead (CLAUDE.md rule 5:
            site identifiers only, never person names). */}
        <StateWord state="NOT_MEASURED" reason={NO_ACK_STORE} />
      </td>
    </tr>
  );
}

/* ── Footer ──────────────────────────────────────────────────────── */

function Footer({ sitesInScope, windowCount, period, zone }: {
  sitesInScope: number;
  windowCount: number;
  period?: { start?: string; end?: string; start_at?: string; end_at?: string };
  /** The zone the rows share, or null when they span several — then the window
   *  is stated in the fallback zone and labelled as such, never in one site's. */
  zone: string | null;
}): ReactNode {
  const locale = useLocale();
  // Only what the response actually states: the scope size, the row total and the
  // server's own window. The retention claim is gone — no retention policy is
  // published by the platform, so the screen must not promise one.
  //
  // `start_at`/`end_at` are instants; `start`/`end` are instants on a rolling
  // window but LOCAL DATES on a named one, and a date put through a timestamp
  // formatter reads as that date's midnight in this zone — a window end hours
  // before the alerts it contains. So the instants win wherever the server sends
  // them, and the dates are the fall-back for the rolling case that has none.
  const from = period?.start_at ?? period?.start;
  const until = period?.end_at ?? period?.end;
  const window = from && until
    ? `${formatStamp(from, zone, locale)} to ${formatStamp(until, zone, locale)} `
      + `(${zoneLabel(zone, locale)})`
    : null;
  return (
    <div className="muted" style={{ fontSize: 12, padding: "4px 0", lineHeight: 1.5 }}>
      {sitesInScope} sites in scope · {windowCount} distinct alerts in window
      {window ? ` · ${window}` : ""}
    </div>
  );
}

/* ── Helpers ──────────────────────────────────────────────────────── */

/** Device-class buckets, matched on the device identifier the response carries.
 *  This is a filter over text the server sent, not a derived class: the alerts
 *  contract has no `device_class` field (see the report). */
const DEVICE_CLASS_LABEL: { key: Exclude<DeviceClass, "all">; label: string }[] = [
  { key: "inverter", label: "Inverter" },
  { key: "logger", label: "Logger" },
  { key: "transformer", label: "Transformer" },
  { key: "meter", label: "Meter" },
  { key: "weather", label: "Weather station" },
];

function matchesDeviceClass(device: string | undefined, cls: Exclude<DeviceClass, "all">): boolean {
  return (device ?? "").toLowerCase().includes(cls);
}

function classifyDevice(device?: string): string {
  const hit = DEVICE_CLASS_LABEL.find((c) => matchesDeviceClass(device, c.key));
  return hit ? hit.label : "Other";
}

function sentenceCase(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function formatMeasuredInline(vals?: Record<string, unknown>): string {
  if (!vals) return "";
  const entries = Object.entries(vals).filter(([, v]) => v != null && v !== "");
  if (entries.length === 0) return "";
  return entries.map(([k, v]) => `${k.replace(/_/g, " ")} ${v}`).join(" · ");
}

/**
 * A timestamp in the zone the EVENT belongs to.
 *
 * The required basis is each site's own `platform_sites.timezone`, which the row
 * now carries. The screen used to render every stamp in UTC and say so in the
 * header — honest, but it asked the reader to do the arithmetic on every line of
 * a list whose whole job is "when did this happen". A row with no registered zone
 * still falls back to UTC, and `zoneLabel` names whichever one is in force.
 */
function formatStamp(iso?: string | null, zone?: string | null, locale?: string): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString(locale, {
    timeZone: zone || ZONE_FALLBACK,
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

/** What to CALL a zone in a column header, as the reader's own locale writes it —
 *  "IST" under en-IN, "GMT+5:30" under a locale with no abbreviation for it. The
 *  abbreviation is never hardcoded: it is the zone from the registry, spelled by
 *  the locale from the session (UX-003 §6.2). */
function zoneLabel(zone: string | null | undefined, locale: string): string {
  const tz = zone || ZONE_FALLBACK;
  try {
    const parts = new Intl.DateTimeFormat(locale, { timeZone: tz, timeZoneName: "short" })
      .formatToParts(new Date());
    return parts.find((p) => p.type === "timeZoneName")?.value ?? tz;
  } catch {
    return tz;
  }
}

/** The one zone a set of rows share, or null when they span several — a header
 *  may name a zone only when every stamp beneath it is in that zone.
 *
 *  Null for an EMPTY list too, and that is the case that bit: zero rows means zero
 *  zones, the caller fell through to UTC, and a three-minute window just after
 *  local midnight was labelled in a zone nobody here reads. The caller now falls
 *  back to the scope's own zone, which the response carries for exactly this. */
function sharedZone(alerts: EnvelopeDeviceAlertsDataAlertsItem[]): string | null {
  const zones = new Set(alerts.map((a) => a.timezone || ZONE_FALLBACK));
  return zones.size === 1 ? [...zones][0]! : null;
}

/** Last seen, with "now" for a fresh row. An elapsed duration needs no zone; the
 *  stamp it falls back to does. */
function formatLastSeen(iso?: string | null, zone?: string | null, locale?: string): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const diffMin = Math.floor((Date.now() - d.getTime()) / 60_000);
  if (diffMin < 2) return "now";
  return formatStamp(iso, zone, locale);
}

function exportAlertsCsv(
  alerts: EnvelopeDeviceAlertsDataAlertsItem[],
  severityFilter: SeverityFilter,
  stateFilter: StateFilter,
  deviceClass: DeviceClass,
  locale: string,
): void {
  let list = [...alerts];

  if (severityFilter === "critical") list = list.filter((a) => a.severity === 2);
  else if (severityFilter === "warning") list = list.filter((a) => a.severity === 1);
  else if (severityFilter === "info") list = list.filter((a) => a.severity === 0);

  if (stateFilter === "active") list = list.filter((a) => a.active);
  else if (stateFilter === "cleared") list = list.filter((a) => !a.active);

  if (deviceClass !== "all") {
    list = list.filter((a) => matchesDeviceClass(a.device, deviceClass));
  }

  // Each row carries the zone its stamps are in, because the rows may not share
  // one — a single zone in the header would be a claim about the whole file.
  const header = "Severity,Site,Device,Alert,Panel Code,Fault Level,Register,"
    + "Measured Values,Count,First Seen,Last Seen,Cleared At,Timezone,State,"
    + "Acknowledged";
  const rows = list.map((a) => {
    const state = a.active ? "Active" : "Cleared";
    const vals = formatMeasuredInline(a.measured_values);
    const csvEscape = (s: string) => `"${s.replace(/"/g, '""')}"`;
    return [
      csvEscape(a.severity_label ?? "CANNOT_CHECK"),
      csvEscape(a.site ?? ""),
      csvEscape(a.device ?? ""),
      // The name a person reads, then the register and raw values a person
      // CHASES — an export is read by both, so it carries both.
      csvEscape(a.label ?? ""),
      csvEscape(a.display_code ?? ""),
      csvEscape(a.fault_level_label ?? ""),
      csvEscape(a.event_type ?? ""),
      csvEscape(vals),
      String(a.count ?? 1),
      csvEscape(formatStamp(a.first_seen, a.timezone, locale)),
      csvEscape(formatStamp(a.last_seen, a.timezone, locale)),
      csvEscape(a.cleared_at ? formatStamp(a.cleared_at, a.timezone, locale) : ""),
      csvEscape(a.timezone ?? ZONE_FALLBACK),
      state,
      // No ack store: the export states the seam rather than exporting a blank
      // cell that reads as "not acknowledged".
      "NOT_MEASURED",
    ].join(",");
  });

  const csv = [header, ...rows].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `alerts-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/* ── Styles ──────────────────────────────────────────────────────── */

/** The bar above a grouped table: how many sections, and one control for all. */
const groupBarStyle: React.CSSProperties = {
  display: "flex", alignItems: "center", justifyContent: "space-between",
  gap: 12, padding: "8px 12px", fontSize: 12,
  color: "var(--sk-color-ink-2)",
  borderBottom: "1px solid var(--sk-color-border-1)",
};

/** A control that reads as text, not a button — it sits inside a table chrome
 *  row where a filled button would outweigh the data beneath it. */
const linkButtonStyle: React.CSSProperties = {
  background: "none", border: "none", padding: 0, cursor: "pointer",
  font: "inherit", color: "var(--sk-color-accent)", textDecoration: "underline",
};

/** The group header's disclosure control. Inherits the header's own type so the
 *  section name does not change weight or size when it becomes clickable. */
const groupToggleStyle: React.CSSProperties = {
  background: "none", border: "none", padding: 0, cursor: "pointer",
  font: "inherit", color: "inherit", display: "inline-flex",
  alignItems: "baseline", gap: 6,
};

const pageTitleStyle: React.CSSProperties = {
  fontSize: "var(--sk-type-title-size, 18px)",
  fontWeight: 600,
  margin: 0,
  color: "var(--sk-color-ink-1)",
};

const exportBtnStyle: React.CSSProperties = {
  fontSize: 12,
  padding: "6px 14px",
  border: "1px solid var(--sk-color-border-2)",
  borderRadius: "var(--sk-radius-control, 6px)",
  background: "var(--sk-color-surface-0)",
  color: "var(--sk-color-ink-1)",
  cursor: "pointer",
  fontWeight: 500,
};

const timeCtlStyle: React.CSSProperties = {
  marginLeft: "auto",
  display: "flex",
  gap: 2,
  background: "var(--sk-color-surface-2)",
  borderRadius: "var(--sk-radius-control, 6px)",
  padding: 2,
};

const timeCtlItemStyle: React.CSSProperties = {
  padding: "4px 10px",
  borderRadius: 5,
  color: "var(--sk-color-ink-2)",
  fontSize: 12,
  fontWeight: 400,
  cursor: "pointer",
  border: "none",
  background: "transparent",
};

const timeCtlActiveStyle: React.CSSProperties = {
  ...timeCtlItemStyle,
  background: "var(--sk-color-surface-0)",
  color: "var(--sk-color-ink-1)",
  fontWeight: 600,
  boxShadow: "var(--sk-elevation-1)",
};

const summaryGridStyle: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
  gap: 10,
};

const summaryCardStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 4,
  padding: "12px 14px",
};

const summaryLabelStyle: React.CSSProperties = {
  fontSize: 10,
  fontWeight: 600,
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  color: "var(--sk-color-ink-3)",
};

const summaryValueStyle: React.CSSProperties = {
  fontFamily: "var(--sk-font-mono)",
  fontSize: 21,
  fontWeight: 300,
  color: "var(--sk-color-ink-1)",
  fontVariantNumeric: "tabular-nums",
  lineHeight: 1.15,
  whiteSpace: "nowrap",
  marginTop: 2,
};

const filterRowStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  flexWrap: "wrap",
};

const filterLabelStyle: React.CSSProperties = {
  fontSize: 11,
  color: "var(--sk-color-ink-2)",
  marginLeft: 8,
};

const chipBase: React.CSSProperties = {
  height: 18,
  padding: "0 6px",
  borderRadius: "var(--sk-radius-chip, 4px)",
  border: "1px solid var(--sk-color-border-2)",
  background: "var(--sk-color-surface-0)",
  color: "var(--sk-color-ink-2)",
  fontSize: 10,
  fontWeight: 500,
  cursor: "pointer",
  whiteSpace: "nowrap",
  display: "inline-flex",
  alignItems: "center",
};

const bulkBarStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  padding: "6px 12px",
  background: "var(--sk-color-accent-soft)",
  borderRadius: "var(--sk-radius-control, 6px)",
  border: "1px solid var(--sk-color-accent)",
  flexWrap: "wrap",
};

const disabledActionStyle: React.CSSProperties = {
  fontSize: 12,
  padding: "4px 12px",
  color: "var(--sk-color-ink-3)",
  cursor: "not-allowed",
};

const tableStyle: React.CSSProperties = {
  width: "100%",
  borderCollapse: "collapse",
  fontSize: 11.5,
};

const thStyle: React.CSSProperties = {
  textAlign: "left",
  fontSize: 10,
  fontWeight: 600,
  textTransform: "uppercase",
  letterSpacing: "0.05em",
  color: "var(--sk-color-ink-3)",
  padding: "6px 8px",
  borderBottom: "1px solid var(--sk-color-border-2)",
  whiteSpace: "nowrap",
};

const tdStyle: React.CSSProperties = {
  padding: "7px 8px",
  borderBottom: "1px solid var(--sk-color-border-1)",
  color: "var(--sk-color-ink-1)",
  verticalAlign: "middle",
  height: "var(--sk-row, 40px)",
};

// The group-header roll-up is body text, not a KPI tile, so the figure renders
// at the header's own size. Metric reads its size from these custom properties;
// overriding them here (rather than in the shared stylesheet) scopes the change
// to this one roll-up and leaves every other Metric at its tile size.
const stoppedMetricStyle = {
  "--sk-type-metric-size": "var(--sk-type-body-size)",
  "--sk-type-metric-weight": "var(--sk-type-body-weight)",
} as React.CSSProperties;

const siteGroupHeaderStyle: React.CSSProperties = {
  padding: "8px 10px",
  background: "var(--sk-color-surface-2)",
  fontWeight: 600,
  fontSize: 12,
  color: "var(--sk-color-ink-1)",
  borderBottom: "1px solid var(--sk-color-border-1)",
};

const dropdownMenuStyle: React.CSSProperties = {
  position: "absolute",
  top: "calc(100% + 4px)",
  left: 0,
  zIndex: 80,
  minWidth: 160,
  padding: "4px 0",
  background: "var(--sk-color-surface-0)",
  border: "1px solid var(--sk-color-border-1)",
  borderRadius: 8,
  boxShadow: "var(--sk-shadow-float)",
};

const dropItemStyle: React.CSSProperties = {
  display: "block",
  width: "100%",
  padding: "6px 12px",
  border: 0,
  fontSize: 12,
  color: "var(--sk-color-ink-1)",
  cursor: "pointer",
  textAlign: "left",
  whiteSpace: "nowrap",
};
